/**
 * @fileoverview useBudgetInfo - Budget metadata fetching hook.
 *
 * Fetches and caches budget metadata (name, start month, role) for the currently
 * selected budget. Automatically refreshes when budget selection changes.
 *
 * @description This hook provides:
 * - Budget info fetching from API
 * - Automatic refresh on budget change
 * - Budget metadata caching
 * - Silent error handling
 * - Manual refresh function for after settings updates
 *
 * Budget info structure:
 * {
 *   budgetName: "Family Budget",
 *   startMonth: "2025-01-01",
 *   role: "owner" | "elevated_member" | "member",
 *   fillNegativeFirst: false,
 *   allowOverTarget: false
 * }
 *
 * @module hooks/useBudgetInfo
 */
import { useState, useEffect, useCallback } from 'react';
import { budgetAPI } from '../services/api';

/**
 * Hook for fetching budget metadata.
 *
 * @param {Object|null} selectedBudget - Currently selected budget object
 * @returns {Object} Budget info and refresh function
 * @returns {string} return.budgetName - Budget name
 * @returns {string} return.startMonth - Budget start month (YYYY-MM-DD)
 * @returns {string} return.role - User's role in budget
 * @returns {boolean} return.fillNegativeFirst - Fill negative categories first setting
 * @returns {boolean} return.allowOverTarget - Allow over target setting
 * @returns {Function} return.refreshBudgetInfo - Manually refresh budget info
 *
 * @example
 * const budgetInfo = useBudgetInfo(selectedBudget);
 * if (budgetInfo) {
 *   console.log(budgetInfo.budgetName);
 *   console.log(budgetInfo.role); // "owner", "elevated_member", or "member"
 * }
 */
export const useBudgetInfo = (selectedBudget) => {
  const [budgetInfo, setBudgetInfo] = useState(null);

  const fetchBudgetInfo = useCallback(async () => {
    if (!selectedBudget) return;

    try {
      const info = await budgetAPI.getInfo();
      const enhancedInfo = {
        ...info,
        isCeased: info.ceaseDate !== null
      };
      setBudgetInfo(enhancedInfo);
    } catch (error) {
      // Silently fail
    }
  }, [selectedBudget]);

  useEffect(() => {
    fetchBudgetInfo();
  }, [fetchBudgetInfo]);

  // Return an object with budgetInfo properties spread and refresh function
  // This maintains backward compatibility while adding the refresh function
  if (budgetInfo === null) {
    return null;
  }

  return {
    ...budgetInfo,
    refreshBudgetInfo: fetchBudgetInfo
  };
};
